import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, Star, MapPin, ChefHat, Quote } from "lucide-react";
import { restaurantInfo, galleryImages, galleryAlt, testimonials, values, menuItems } from "../mock";
import SectionHeading from "../components/SectionHeading";
import HeroSlider from "../components/HeroSlider";
import Reveal from "../components/Reveal";
import SEO, { restaurantSchema } from "../components/SEO";
import RestaurantjiBadge from "../components/RestaurantjiBadge";
import FAQ from "../components/FAQ";
import VisitSection from "../components/VisitSection";
import { faqs } from "../data/faqs";
import { faqSchema } from "../components/SEO";
import { Wheat, Sprout, Heart, Flame, Utensils } from "lucide-react";
import { useT, useLang } from "../context/LanguageContext";
import { HOME_STRINGS, NAV_STRINGS, SEO_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const iconMap = { Wheat, Sprout, Heart, Flame };

const marqueeItems = [
  "Handmade Tortillas",
  "Family Recipes",
  "Fresh Salsas Daily",
  "Los Gatos, CA",
  "Traditional Mexican",
  "Auténtico",
];

// Hero slideshow — tacos, quesadilla, sope, salad, taco americano (no burritos)
const heroSlides = [
  "https://static.wixstatic.com/media/ea1028_15cb7cced9174b7e9dc5b3a9f73fd86e~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/tacos.jpg",
  "https://static.wixstatic.com/media/ea1028_0a6e22be098f4c1b9dd8657a0da939f7~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/quesadilla.jpg",
  "https://static.wixstatic.com/media/ea1028_23d325092fb44ffb9fc97df3cc5aa65e~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/sope.jpg",
  "https://static.wixstatic.com/media/ea1028_39f1ba77764c4096bd8347f0c54fce9b~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/salad.jpg",
  "https://static.wixstatic.com/media/ea1028_4b835c7c409c4df794329784f15646a0~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_90,enc_avif,quality_auto/taco-americano.jpg",
];

// Explicit signature dish IDs (Meat Quesadilla · Cheese & Meat Nachos · Street Taco)
const SIGNATURE_IDS = [6, 61, 40];

export default function Home() {
  const t = useT();
  const { lang } = useLang();
  const isEs = lang === "es";
  const signatureDishes = SIGNATURE_IDS
    .map((id) => menuItems.find((m) => m.id === id))
    .filter(Boolean);
  const gallery = galleryImages;

  return (
    <>
      <SEO
        title={SEO_STRINGS.home.title.en}
        titleEs={SEO_STRINGS.home.title.es}
        description={SEO_STRINGS.home.description.en}
        descriptionEs={SEO_STRINGS.home.description.es}
        path="/"
        keywords="Mexican restaurant Los Gatos, authentic tacos, taqueria Los Gatos, best Mexican food Bay Area, handmade tortillas, family-owned restaurant, Nina's Taqueria, burritos Los Gatos, catering Mexican food"
        keywordsEs="restaurante mexicano Los Gatos, tacos auténticos, taquería Los Gatos, mejor comida mexicana Bay Area, tortillas hechas a mano, restaurante familiar, Nina's Taqueria, burritos Los Gatos, catering mexicano"
        schema={{
          "@context": "https://schema.org",
          "@graph": [restaurantSchema, faqSchema(faqs, lang)],
        }}
      />
      <main className="overflow-hidden">
      {/* HERO with slideshow */}
      <section className="relative min-h-[92vh] flex items-center pt-24">
        <HeroSlider images={heroSlides} interval={5500} />
        {/* Lighter overlay so the food shows through */}
        <div className="absolute inset-0 bg-gradient-to-r from-granite-dark/85 via-granite-dark/45 to-granite-dark/10 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-granite-dark/50 via-transparent to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 w-full py-20 z-10">
          <div className="text-cream max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream/15 backdrop-blur border border-cream/25 text-[11px] uppercase tracking-[0.2em] mb-6 fade-in-up">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-light animate-pulse" />
              {t(HOME_STRINGS.heroEyebrow.en, HOME_STRINGS.heroEyebrow.es)}
            </div>
            <div className="font-hand text-3xl md:text-4xl text-brand-light mb-3 fade-in-up">{t(HOME_STRINGS.heroWelcome.en, HOME_STRINGS.heroWelcome.es)}</div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-[92px] leading-[0.95] font-semibold fade-in-up">
              Nina's <br /> Taqueria
            </h1>
            <p className="mt-6 text-base md:text-lg text-cream/90 leading-relaxed max-w-lg fade-in-up">
              {t(HOME_STRINGS.heroSub.en, HOME_STRINGS.heroSub.es)}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-in-up">
              <a
                href={restaurantInfo.externalLinks.orderOnline}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("order_online_click", { source: "home_hero" })}
                data-testid="home-hero-order-btn"
                className="order-cta group inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-cream px-7 py-3.5 rounded-full font-semibold text-[15px] transition-all shadow-xl hover:-translate-y-0.5"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cream/70 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cream" />
                </span>
                {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)}
                <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to="/menu"
                className="group inline-flex items-center gap-2 border border-cream/50 hover:border-cream text-cream px-7 py-3.5 rounded-full font-medium text-[15px] transition-all backdrop-blur-sm hover:bg-cream/15"
              >
                {t(HOME_STRINGS.heroMenuCta.en, HOME_STRINGS.heroMenuCta.es)}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="font-hand text-lg md:text-xl text-brand-light mt-5">
              {t(HOME_STRINGS.heroPromoPre.en, HOME_STRINGS.heroPromoPre.es)} <span className="text-cream font-bold">5OFFLINE</span> — {t(HOME_STRINGS.heroPromoPost.en, HOME_STRINGS.heroPromoPost.es)}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-brand text-cream py-4 border-y border-brand-dark/40 marquee-wrapper">
        <div className="marquee-track font-display text-xl md:text-2xl">
          {Array.from({ length: 2 }).map((_, dupe) => (
            <div key={dupe} className="flex items-center shrink-0">
              {marqueeItems.map((label, i) => (
                <div key={`${dupe}-${i}`} className="marquee-item">
                  <span className="w-1.5 h-1.5 rounded-full bg-cream/70" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32 bg-cream-50">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={heroSlides[0]}
                alt="Handmade Mexican tacos at Nina's Taqueria"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-10 w-52 md:w-64 rounded-2xl overflow-hidden shadow-2xl border-8 border-cream">
              <img src="https://static.wixstatic.com/media/ea1028_bf784f518db041628e74bd07af66f946~mv2.jpg" alt="Fresh guacamole with lime and cilantro" className="w-full h-full object-cover aspect-square" />
            </div>
            <div
              className="sticker absolute -top-6 -left-4 bg-brand text-cream px-6 py-4 rounded-2xl shadow-xl cursor-default"
              style={{ "--rot": "-4deg", transform: "rotate(-4deg)" }}
              data-testid="sticker-family-owned"
            >
              <div className="font-hand text-3xl leading-none">{t(HOME_STRINGS.familyOwned.en, HOME_STRINGS.familyOwned.es)}</div>
              <div className="text-xs uppercase tracking-widest mt-1">{t(HOME_STRINGS.traditional.en, HOME_STRINGS.traditional.es)}</div>
            </div>

            {/* Clean Restaurantji recognition badge (no frame) */}
            <div
              className="sticker absolute -bottom-6 -left-3 md:-left-6 bg-cream shadow-xl rounded-2xl px-3 py-2"
              style={{ "--rot": "-3deg", transform: "rotate(-3deg)", animationDelay: "1.2s" }}
              data-testid="sticker-restaurantji"
            >
              <RestaurantjiBadge variant="light" />
            </div>
          </Reveal>

          <div>
            <Reveal delay={1}>
              <SectionHeading
                align="left"
                eyebrow={t(HOME_STRINGS.storyEyebrow.en, HOME_STRINGS.storyEyebrow.es)}
                title={t(HOME_STRINGS.storyTitle.en, HOME_STRINGS.storyTitle.es)}
                subtitle={t(HOME_STRINGS.storySub.en, HOME_STRINGS.storySub.es)}
              />
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {values.map((v, i) => {
                const Icon = iconMap[v.icon] || Utensils;
                return (
                  <Reveal key={v.title} delay={((i % 3) + 1)}>
                    <div className="p-5 rounded-2xl bg-cream border border-cream-100 hover:border-brand/40 transition-all group hover:-translate-y-1 hover:shadow-md h-full">
                      <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center mb-3 group-hover:bg-brand group-hover:text-cream transition-colors text-brand">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="font-display text-lg font-semibold text-granite-dark">{isEs ? v.titleEs : v.title}</div>
                      <p className="text-sm text-granite/70 mt-1.5 leading-relaxed">{isEs ? v.descriptionEs : v.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={2}>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-2 text-granite-dark font-medium group"
              >
                <span className="border-b border-granite-dark group-hover:border-brand group-hover:text-brand transition-colors pb-0.5">
                  {t(HOME_STRINGS.storyMeet.en, HOME_STRINGS.storyMeet.es)}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 group-hover:text-brand transition-all" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ORDER STRIP */}
      <section className="relative py-14 bg-brand text-cream overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-cream/15 border border-cream/30 flex items-center justify-center shrink-0">
              <ChefHat className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display text-2xl md:text-3xl font-semibold leading-tight">{t(HOME_STRINGS.orderStripTitle.en, HOME_STRINGS.orderStripTitle.es)}</div>
              <div className="text-cream/85 text-sm md:text-base mt-1">{t(HOME_STRINGS.orderStripSub.en, HOME_STRINGS.orderStripSub.es)}</div>
            </div>
          </div>
          <a
            href={restaurantInfo.externalLinks.orderOnline}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-cream text-brand-dark hover:bg-white px-7 py-3.5 rounded-full font-semibold text-[15px] transition-all shadow-xl hover:-translate-y-0.5 shrink-0"
          >
            {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)} <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* SIGNATURE DISHES */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <SectionHeading
              eyebrow={t(HOME_STRINGS.signatureEyebrow.en, HOME_STRINGS.signatureEyebrow.es)}
              title={t(HOME_STRINGS.signatureTitle.en, HOME_STRINGS.signatureTitle.es)}
              subtitle={t(HOME_STRINGS.signatureSub.en, HOME_STRINGS.signatureSub.es)}
            />
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {signatureDishes.map((dish, i) => (
              <Reveal key={dish.id} delay={(i % 3) + 1}>
                <div className="dish-card group relative rounded-3xl overflow-hidden bg-cream-50 border border-cream-100 shadow-sm hover:shadow-xl h-full">
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img
                      src={dish.image}
                      alt={isEs ? dish.nameEs : dish.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-granite-dark/95 via-granite-dark/30 to-transparent" />
                    <div className="absolute top-4 left-4 bg-brand text-cream text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full font-semibold shadow-lg">
                      #{i + 1} {t(HOME_STRINGS.signatureBadge.en, HOME_STRINGS.signatureBadge.es)}
                    </div>
                    <div className="absolute bottom-6 left-6 right-6 text-cream">
                      <h3 className="font-display text-2xl font-semibold">{isEs ? dish.nameEs : dish.name}</h3>
                      <div className="text-cream/85 text-sm mt-1">${dish.price.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-granite/70 leading-relaxed">{isEs ? dish.descriptionEs : dish.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-14 flex flex-wrap justify-center gap-3">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 bg-granite-dark text-cream px-7 py-3.5 rounded-full font-medium hover:bg-granite transition-all hover:-translate-y-0.5 shadow-md"
            >
              {t(HOME_STRINGS.signatureExplore.en, HOME_STRINGS.signatureExplore.es)}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={restaurantInfo.externalLinks.orderOnline}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand text-cream px-7 py-3.5 rounded-full font-semibold hover:bg-brand-dark transition-all hover:-translate-y-0.5 shadow-md"
            >
              {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)} <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* GALLERY — clean 4-col grid, 8 images */}
      <section className="py-24 md:py-32 bg-cream-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <SectionHeading
              eyebrow={t(HOME_STRINGS.galleryEyebrow.en, HOME_STRINGS.galleryEyebrow.es)}
              title={t(HOME_STRINGS.galleryTitle.en, HOME_STRINGS.galleryTitle.es)}
              subtitle={t(HOME_STRINGS.gallerySub.en, HOME_STRINGS.gallerySub.es)}
            />
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery.map((src, i) => (
              <Reveal key={i} delay={((i % 3) + 1)}>
                <div className="aspect-square overflow-hidden rounded-2xl group">
                  <img
                    src={src}
                    alt={t(galleryAlt[i]?.en || "Nina's Taqueria dish", galleryAlt[i]?.es || "Platillo de Nina's Taqueria")}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-brand-dark text-cream relative overflow-hidden">
        {/* Decorative granite blobs + noise */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-brand/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-brand-light/15 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 rounded-full bg-brand-light/50 pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/5 w-1.5 h-1.5 rounded-full bg-cream/40 pointer-events-none" />
        {/* Hand-lettered accent */}
        <div className="absolute top-16 right-8 md:right-16 font-hand text-5xl md:text-6xl text-brand-light/25 rotate-[-8deg] pointer-events-none select-none">
          ¡Gracias!
        </div>
        <div className="absolute inset-0 bg-noise opacity-15 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Reveal>
            <SectionHeading
              light
              eyebrow={t(HOME_STRINGS.lovedByEyebrow.en, HOME_STRINGS.lovedByEyebrow.es)}
              title={t(HOME_STRINGS.lovedByTitle.en, HOME_STRINGS.lovedByTitle.es)}
              subtitle={t(HOME_STRINGS.lovedBySub.en, HOME_STRINGS.lovedBySub.es)}
            />
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.id} delay={(i % 3) + 1}>
                <div className="relative p-6 rounded-2xl bg-granite-dark/50 backdrop-blur border border-cream/15 hover:border-brand-light/50 transition-all hover:-translate-y-1 h-full">
                  <Quote className="absolute -top-3 -left-3 w-8 h-8 p-1.5 rounded-full bg-brand text-cream shadow-lg" />
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: tm.rating }).map((_, k) => (
                      <Star key={k} className="w-4 h-4 fill-brand-light text-brand-light" />
                    ))}
                  </div>
                  <p className="text-cream/90 text-[15px] leading-relaxed">“{isEs ? (tm.textEs || tm.text) : tm.text}”</p>
                  <div className="mt-5 pt-5 border-t border-cream/10 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-light text-brand-dark font-display text-sm font-semibold flex items-center justify-center shrink-0">
                      {tm.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-display text-cream leading-tight">{tm.name}</div>
                      <div className="text-cream/60 text-xs mt-0.5">{tm.location}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Small "leave a review" strip */}
          <Reveal delay={2}>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-3 text-sm">
              <span className="text-cream/70">{t(HOME_STRINGS.reviewsIntro.en, HOME_STRINGS.reviewsIntro.es)}</span>
              <a
                href={restaurantInfo.socials.yelp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cream/10 border border-cream/20 hover:bg-cream/20 transition-colors text-cream"
              >
                {t(HOME_STRINGS.reviewYelp.en, HOME_STRINGS.reviewYelp.es)} <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href="https://www.google.com/maps?cid=12270814832204351491"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cream/10 border border-cream/20 hover:bg-cream/20 transition-colors text-cream"
              >
                {t(HOME_STRINGS.reviewGoogle.en, HOME_STRINGS.reviewGoogle.es)} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Visit us — unified map + reviews + hours + CTAs (replaces the previous
          GoogleReviewsWidget + HomeMap + "Come hungry" sections) */}
      <VisitSection />
      </main>
    </>
  );
}
